import { createFileRoute, Outlet, redirect, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { Building2, LayoutDashboard, FileText, ShoppingCart, PackagePlus, User, LogOut } from "lucide-react";
import { getSupabase } from "@/integrations/external-supabase/client";

export const Route = createFileRoute("/vendor")({
  ssr: false,
  beforeLoad: async () => {
    const supabase = getSupabase();
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) throw redirect({ to: "/vendor-login" });
    const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", data.user.id);
    const isVendor = (roles ?? []).some((r: any) => r.role === "vendor");
    if (!isVendor) throw redirect({ to: "/vendor-login" });
    return { user: data.user };
  },
  component: VendorShell,
});

type NavItem = { to: string; label: string; icon: any; exact?: boolean };
const NAV: NavItem[] = [
  { to: "/vendor", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/vendor/rfqs", label: "RFQs", icon: FileText },
  { to: "/vendor/purchase-orders", label: "Purchase Orders", icon: ShoppingCart },
  { to: "/vendor/grns", label: "Deliveries (GRN)", icon: PackagePlus },
  { to: "/vendor/profile", label: "My Profile", icon: User },
];

function VendorShell() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  async function handleSignOut() {
    const { signOut } = await import("@/integrations/api/auth");
    await signOut();
    navigate({ to: "/vendor-login", replace: true });
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden w-64 shrink-0 border-r border-border bg-card md:flex md:flex-col">
        <div className="flex items-center gap-2 border-b border-border px-4 py-4">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground">
            <Building2 className="h-4 w-4" />
          </span>
          <div>
            <p className="text-sm font-bold">Vendor Portal</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">HAMS</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {NAV.map((n) => {
            const active = n.exact ? pathname === n.to : pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
                  active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-accent"
                }`}
              >
                <Icon className="h-4 w-4" />
                {n.label}
              </Link>
            );
          })}
        </nav>
        <button
          onClick={handleSignOut}
          className="m-3 flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-accent"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </aside>
      <main className="flex-1 overflow-x-hidden">
        <div className="mx-auto max-w-6xl p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
